<template>
  <div class="email-categories">
    <div v-for="category in emailCategories" :key="category" class="email-category mb-2">
      <label>{{ category.charAt(0).toUpperCase() + category.slice(1) }}</label>
      <div class="badge-wrapper d-flex align-items-center">
        <div class="badge-container me-2">
          <span v-if="modelValue?.[category]?.length === 0" class="badge-placeholder">
            Geen e-mails
          </span>
          <span v-else v-for="(email, index) in modelValue?.[category]" :key="index" 
                class="badge me-2 d-inline-flex align-items-center bg-white text-dark" 
                :class="badgeClass(category)"
                style="border: 2px solid currentColor;"
                @click="openEmailDialog(category, index)"
                :title="email.email">
            {{ email.name || email.email }}
            <i class="bi bi-x-lg ms-2" 
               @click.stop="deleteEmailFromBadge(category, index)"
               style="cursor: pointer;"></i>
          </span>
        </div>
        <button type="button" @click="openEmailDialog(category, -1)" class="btn btn-sm btn-secondary">
          <i class="bi bi-plus-lg d-md-none"></i>
          <span class="d-none d-md-inline">Toevoegen</span>
        </button>
      </div>
    </div>

    <!-- Bootstrap 5 Modal -->
    <div class="modal fade" id="emailModal" tabindex="-1" aria-labelledby="emailModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="emailModalLabel">Email Bewerken</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="cancelEditEmail"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="editName" class="form-label">Naam</label>
              <input type="text" class="form-control" id="editName" v-model="editingEmail.name">
            </div>
            <div class="mb-3">
              <label for="editEmail" class="form-label">E-mail</label>
              <input type="email" class="form-control" id="editEmail" v-model="editingEmail.email">
            </div>
            <div class="mb-3 form-check">
              <input type="checkbox" class="form-check-input" id="applyAll" v-model="applyToAllCategories">
              <label class="form-check-label" for="applyAll">
                Pas deze wijziging toe op alle e-mailcategorieën
              </label>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal" @click="cancelEditEmail">Annuleren</button>
            <button type="button" class="btn btn-secondary" @click="saveEmail">Bewaar</button>
            <button type="button" class="btn btn-danger" v-if="editingEmailIndex >= 0" @click="deleteEmailFromModal">Verwijder</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Utils from '@/utils/Utils';

export default {
  name: 'EmailManager',
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    emailCategories: {
      type: Array,
      default: () => ['success', 'notification', 'error', 'admin', 'error_invoice']
    }
  },
  data() {
    return {
      editingEmail: { name: '', email: '' },
      editingEmailCategory: '',
      editingEmailIndex: -1,
      applyToAllCategories: false,
    };
  },
  methods: {
    badgeClass(category) {
      switch (category) {
        case 'success':
          return 'border-success';
        case 'notification':
          return 'border-warning';
        case 'error':
          return 'border-red';
        case 'admin':
          return 'border-black';
        case 'error_invoice':
          return 'border-red';
        default:
          return '';
      }
    },
    openEmailDialog(category, index) {
      this.editingEmailCategory = category;
      this.editingEmailIndex = index;
      if (index >= 0) {
        this.editingEmail = { ...this.modelValue[category][index] };
      } else {
        this.editingEmail = { name: '', email: '' }; // Lege velden voor nieuwe e-mail
      }
      const modalElement = document.getElementById('emailModal');
      if (modalElement) {
        const modal = new window.bootstrap.Modal(modalElement);
        modal.show();
      } else {
        console.error('Modal element not found');
      }
    },
    async saveEmail() {
      if (!this.editingEmail.email || !Utils.isValidEmail(this.editingEmail.email)) {
        this.$emit('error', 'Vul een geldig e-mailadres in');
        return;
      }

      try {
        // Maak een kopie van de modelValue om te bewerken
        const updatedEmails = JSON.parse(JSON.stringify(this.modelValue));
        
        if (this.applyToAllCategories) {
          if (this.editingEmailIndex >= 0) {
            // Update bestaande email in alle categorieën
            this.emailCategories.forEach(category => {
              const emailIndex = updatedEmails[category].findIndex(
                email => email.email === this.editingEmail.email
              );
              if (emailIndex >= 0) {
                updatedEmails[category][emailIndex] = { ...this.editingEmail };
              }
            });
          } else {
            // Nieuwe email toevoegen aan categorieën waar hij nog niet bestaat
            this.emailCategories.forEach(category => {
              const exists = updatedEmails[category].some(
                email => email.email === this.editingEmail.email
              );
              if (!exists) {
                updatedEmails[category].push({ ...this.editingEmail });
              }
            });
          }
        } else {
          if (this.editingEmailIndex >= 0) {
            updatedEmails[this.editingEmailCategory][this.editingEmailIndex] = { ...this.editingEmail };
          } else {
            const exists = updatedEmails[this.editingEmailCategory].some(
              email => email.email === this.editingEmail.email
            );
            if (!exists) {
              updatedEmails[this.editingEmailCategory].push({ ...this.editingEmail });
            }
          }
        }

        // Emit de update-gebeurtenis met de nieuwe waarden
        this.$emit('update:modelValue', updatedEmails);
        this.$emit('save', {
          email: this.editingEmail,
          isUpdate: this.editingEmailIndex >= 0
        });
        
        const modal = window.bootstrap.Modal.getInstance(document.getElementById('emailModal'));
        modal.hide();
        this.applyToAllCategories = false;
      } catch (error) {
        console.log('saveEmail error', error);
        this.$emit('error', 'Er is een fout opgetreden bij het opslaan van de e-mail.');
      }
    },
    async deleteEmailFromBadge(category, index) {
      try {
        // Maak een kopie van de modelValue om te bewerken
        const updatedEmails = JSON.parse(JSON.stringify(this.modelValue));
        
        // Verwijder specifieke email uit de categorie met splice
        updatedEmails[category].splice(index, 1);
        
        // Emit de update-gebeurtenis met de nieuwe waarden
        this.$emit('update:modelValue', updatedEmails);
        this.$emit('delete');
      } catch (error) {
        this.$emit('error', 'Er is een fout opgetreden bij het verwijderen van de e-mail');
      }
    },
    async deleteEmailFromModal() {
      try {
        // Maak een kopie van de modelValue om te bewerken
        const updatedEmails = JSON.parse(JSON.stringify(this.modelValue));
        
        if (this.applyToAllCategories) {
          // Verwijder email uit alle categorieën
          this.emailCategories.forEach(category => {
            updatedEmails[category] = updatedEmails[category].filter(
              e => e.email !== this.editingEmail.email
            );
          });
        } else {
          // Verwijder email alleen uit de huidige categorie
          updatedEmails[this.editingEmailCategory] = updatedEmails[this.editingEmailCategory].filter(
            e => e.email !== this.editingEmail.email
          );
        }
        
        // Emit de update-gebeurtenis met de nieuwe waarden
        this.$emit('update:modelValue', updatedEmails);
        this.$emit('delete');
        
        const modal = window.bootstrap.Modal.getInstance(document.getElementById('emailModal'));
        modal.hide();
        this.applyToAllCategories = false;
      } catch (error) {
        this.$emit('error', 'Er is een fout opgetreden bij het verwijderen van de e-mail');
      }
    },
    cancelEditEmail() {
      const modal = window.bootstrap.Modal.getInstance(document.getElementById('emailModal'));
      modal.hide();
      this.applyToAllCategories = false;  // Reset de checkbox
    }
  }
};
</script>

<style scoped>
.badge-wrapper {
  display: flex;
  align-items: center;
}

.badge-container {
  border: 1px solid #ccc;
  padding: 5px;
  min-height: 40px;
  border-radius: 4px;
  flex-grow: 1;
}

.badge-placeholder {
  color: #999; 
  font-style: normal;
}

.badge {
  margin-right: 0.5rem;
  cursor: pointer;
}

.btn {
  margin-left: 10px;
}

.border-red {
  border-color: #C7066E !important;
}

.border-black {
  border-color: #000000 !important;
}
</style>
